import { AutocompleteProps, InputProps, TextFieldProps } from '@mui/material';
import { HighlightCssClass } from '@provision/rich-text-editor';
import { Fragment, ReactElement } from 'react';
import { normalizeExternalUrl } from 'src/utils/url';
import {
  AutoGeneratedFields,
  DocumentationFormField,
  DocumentationFormValue,
} from './site-documentation.types';
import { SubSystemField } from './sub-system-field';

const hasAISuggestion = ({
  fieldName,
  currentValue,
  defaultValue,
  autoGeneratedFields,
}: {
  fieldName: string;
  currentValue: DocumentationFormValue;
  defaultValue: DocumentationFormValue;
  autoGeneratedFields: AutoGeneratedFields;
}): boolean => {
  return (
    !!autoGeneratedFields &&
    fieldName in autoGeneratedFields &&
    !!autoGeneratedFields[fieldName] &&
    currentValue !== defaultValue &&
    autoGeneratedFields[fieldName] === currentValue
  );
};
export const renderSubSystemField = ({
  field,
  props,
  onFieldChange,
  onResetField,
  autoGeneratedFields,
  getFormValues,
  defaultValue,
}: {
  field: DocumentationFormField;
  props: {
    disabled?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    autocompleteProps?: Partial<AutocompleteProps<any, boolean, true, boolean>>;
    textFieldProps?: Partial<TextFieldProps>;
    inputProps?: Partial<InputProps>;
  };
  onFieldChange: (fieldName: string, value: DocumentationFormValue) => void;
  onResetField: (fieldName: string) => void;
  autoGeneratedFields: AutoGeneratedFields;
  getFormValues: (fieldName: string) => DocumentationFormValue;
  defaultValue: DocumentationFormValue;
}): ReactElement => {
  const currentValue = getFormValues(field.name);

  const link =
    field.icon && typeof field.icon === 'object' && 'name' in field.icon
      ? getUrl(getFormValues(field.icon.name) as string | null | undefined)
      : '';

  const { inputProps, autocompleteProps, textFieldProps } = props;
  return (
    <Fragment key={field.name}>
      <SubSystemField
        field={field}
        link={link}
        disabled={props.disabled}
        autocompleteProps={autocompleteProps}
        textFieldProps={textFieldProps}
        inputProps={inputProps}
        onFieldChange={onFieldChange}
        onResetField={onResetField}
        hasAISuggestion={hasAISuggestion({
          fieldName: field.name,
          currentValue,
          defaultValue,
          autoGeneratedFields,
        })}
      />
    </Fragment>
  );
};
export function getUrl(url: string | null | undefined): string {
  if (!url || typeof url !== 'string') {
    return '';
  }
  return normalizeExternalUrl(url) ?? '';
}

export const normalizeOrgNotes = (orgNotes: string): string =>
  orgNotes
    .replace(
      new RegExp(
        `<span\\s+class=["']${HighlightCssClass}["'][^>]*>([\\s\\S]*?)<\\/span>`,
        'gim',
      ),
      '$1',
    )
    // A truncated AI suggestion can end mid-token and leave control characters behind.
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '');
